import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  updateCustomerActive,
  clearCustomerMessages,
} from "../../../store/customer/customerSlice";
import { getCustomerDisplayName } from "../utils/customerHelpers";

const ToggleCustomerActiveDialog = ({ open, onClose, customer, onSuccess }) => {
  const dispatch = useDispatch();
  const {
    isUpdatingCustomerActive,
    updateCustomerActiveSuccess,
    updateCustomerActiveError,
  } = useSelector((state) => state.customer);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("info");

  const customerName = getCustomerDisplayName(customer);

  const handleConfirmDeactivate = () => {
    if (customer?.orgcode) {
      dispatch(
        updateCustomerActive({
          orgcode: customer.orgcode,
          active: false,
        })
      );
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  React.useEffect(() => {
    if (!open) {
      dispatch(clearCustomerMessages());
    }
  }, [open, dispatch]);

  React.useEffect(() => {
    if (updateCustomerActiveSuccess) {
      const message =
        updateCustomerActiveSuccess.data ||
        updateCustomerActiveSuccess.message ||
        "Customer deactivated successfully.";
      setSnackbarMessage(message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onSuccess?.();
      onClose();
      dispatch(clearCustomerMessages());
    }
  }, [updateCustomerActiveSuccess, onClose, onSuccess, dispatch]);

  React.useEffect(() => {
    if (updateCustomerActiveError) {
      const errorMessage =
        typeof updateCustomerActiveError === "string"
          ? updateCustomerActiveError
          : updateCustomerActiveError.data ||
            updateCustomerActiveError.message ||
            "Failed to deactivate customer.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [updateCustomerActiveError]);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="deactivate-customer-dialog"
        aria-describedby="dialog-for-deactivating-customer"
      >
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 520 },
            maxWidth: 520,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#F5F5F5",
              padding: "16px 24px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#000",
                fontSize: "1.125rem",
              }}
            >
              Deactivate Customer
            </Typography>
            <IconButton
              onClick={onClose}
              disabled={isUpdatingCustomerActive}
              sx={{
                color: "#000",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "#1e293b",
                fontSize: "0.875rem",
                lineHeight: 1.6,
              }}
            >
              Deactivating <strong>{customerName}</strong> will suspend your Billing
              and Invoice Generation. You can reactivate the customer at any time
              from this page.
              <br />
              <br />
              Do you want to continue?
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isUpdatingCustomerActive}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleConfirmDeactivate}
                disabled={isUpdatingCustomerActive}
                startIcon={
                  isUpdatingCustomerActive ? <CircularProgress size={20} /> : null
                }
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {isUpdatingCustomerActive ? "Deactivating..." : "Deactivate"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

ToggleCustomerActiveDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default ToggleCustomerActiveDialog;
